import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, X, ChevronDown, ChevronUp, Loader } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND = 'linear-gradient(135deg,#2cbdc4,#094f6f)';

// ── Checkout helper ───────────────────────────────────────────────────────────
function useCheckout() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const startCheckout = async (packageId) => {
    if (!token) { navigate('/login', { state: { from: '/pricing' } }); return; }
    setLoading(packageId);
    try {
      const res = await fetch(`${API}/api/payments/stripe/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin }),
      });
      const d = await res.json();
      if (d.checkout_url) window.location.href = d.checkout_url;
      else alert(d.detail || 'Payment setup failed. Please try again.');
    } catch { alert('Network error. Please try again.'); }
    finally { setLoading(null); }
  };

  return { loading, startCheckout };
}

// ── Small components ──────────────────────────────────────────────────────────
const Feat = ({ yes, children, note }) => (
  <li className="flex items-start gap-3 py-2.5 border-b border-gray-100 last:border-0">
    <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-black mt-0.5 ${yes ? 'text-white' : 'bg-gray-100 text-gray-400'}`}
      style={yes ? { background: BRAND } : {}}>
      {yes ? '✓' : '–'}
    </span>
    <span className={yes ? 'text-sm text-gray-700' : 'text-sm text-gray-400'}>
      {children}
      {note && <span className="block text-xs text-gray-400 mt-0.5">{note}</span>}
    </span>
  </li>
);

// ── FAQ ───────────────────────────────────────────────────────────────────────
const FAQS = [
  { q: 'What does the founding member fee get me?', a: 'A one-time payment — $5 for students, $10 for professionals. In return you get 20% off VibeMate locked in forever, a founding badge, early access, and a bonus points head-start in the rewards programme. The discount stays active as long as your subscription is active.' },
  { q: 'Is the platform really free to use?', a: 'Yes — browse, search, and book properties completely free. VibeMate is the optional premium subscription that unlocks matching, messaging, rewards, and all advanced features. No pressure to upgrade.' },
  { q: 'How do virtual inspections work?', a: 'Live video walkthroughs conducted by the landlord through RentalX. Book a time slot, join via the app, and get a real-time guided tour. Bundles of 3 cost $19.99. VibeMate subscribers get 1 free inspection included each month.' },
  { q: 'How does landlord commission work?', a: 'Landlords list for free with no monthly fees. A commission of 5–8% of the first month\'s rent is charged only when a tenancy is successfully placed through RentalX. VibeMate landlords pay the lower 5% rate — below the cost of a traditional letting agent.' },
  { q: 'Can I cancel VibeMate anytime?', a: 'Yes — cancel anytime, no lock-in. Your subscription stays active until the end of the current billing period. Founding member pricing reactivates automatically if you resubscribe later.' },
];

export default function PricingPage() {
  const [activeTab, setActiveTab] = useState('student');
  const [openFaq, setOpenFaq] = useState(null);
  const { loading, startCheckout } = useCheckout();

  const tabs = [
    { id: 'student',   label: '🎓 Students' },
    { id: 'pro',       label: '💼 Professionals' },
    { id: 'landlord',  label: '🏠 Landlords' },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'inherit' }}>

      {/* ── Founding banner ── */}
      <div className="py-3 px-4 flex items-center justify-between flex-wrap gap-2"
        style={{ background: 'linear-gradient(135deg,#094f6f,#041e2b)' }}>
        <div className="flex items-center gap-2 text-sm text-white/80">
          <span>💎</span>
          <p>Founding member spots are open. <strong className="text-white">Pay once. Get 20% off VibeMate for life.</strong></p>
        </div>
        <Link to="/founding"
          className="text-xs font-bold px-4 py-1.5 rounded-full border border-[#2cbdc4]/60 text-[#2cbdc4] hover:bg-[#2cbdc4]/10 transition-colors whitespace-nowrap">
          Claim your spot →
        </Link>
      </div>

      {/* ── Nav ── */}
      <div className="border-b border-gray-100 py-4 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-0">
            <span className="text-xl font-black tracking-tight" style={{ color: '#094f6f' }}>Rental</span>
            <img src="/rentalx-x-new.svg" alt="X"
              style={{ height: '1.4rem', width: 'auto', marginLeft: '0.05em', marginBottom: '0.02em', verticalAlign: 'middle' }} />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/how-rewards-work" className="text-sm text-gray-600 hover:text-[#094f6f]">Rewards</Link>
            <Link to="/register/student"
              className="text-sm font-bold px-4 py-2 rounded-xl text-white hover:opacity-90 transition-all"
              style={{ background: BRAND }}>Sign up free</Link>
          </div>
        </div>
      </div>

      {/* ── Hero + tabs ── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-14 pb-8 text-center">
        <p className="text-xs font-bold uppercase tracking-widest text-[#2cbdc4] mb-3">Simple pricing</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 tracking-tight mb-4 leading-tight">
          Start free.<br />
          <span style={{ color: '#094f6f' }}>Upgrade when you&apos;re ready.</span>
        </h1>
        <p className="text-lg text-gray-500 max-w-2xl mx-auto mb-10">
          Browse and book for free. Unlock VibeMate matching, rent rewards, and every premium feature with one simple subscription.
        </p>
        {/* Tab switcher */}
        <div className="inline-flex bg-gray-100 rounded-2xl p-1 gap-1">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${activeTab === t.id ? 'text-white shadow-md' : 'text-gray-500 hover:text-gray-700'}`}
              style={activeTab === t.id ? { background: BRAND } : {}}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">

        {/* ══════════════════ STUDENT ══════════════════ */}
        {activeTab === 'student' && (
          <div className="space-y-8">
            {/* Founding card */}
            <FoundingCard
              title="Lock in 20% off VibeMate — forever."
              desc="Pay a one-time $5 founding fee before launch and your VibeMate drops to $6.39/month for life — even after prices rise. Plus a bonus points head-start on day one."
              perks={['20% off VibeMate forever', '1,000 bonus reward points', 'Founding member badge', 'Early platform access']}
              price="$5" priceSub="AUD · pay once, benefit forever"
            />
            {/* Tier grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <TierCard
                label="Always free" name="Free"
                price="$0" period="/month" note="No credit card required"
                desc="Browse verified listings, book properties, and manage your rental profile — completely free."
                features={[
                  { yes: true, text: 'Create renter profile' },
                  { yes: true, text: 'Browse all verified listings' },
                  { yes: true, text: 'Book properties', note: 'Direct booking with landlords' },
                  { yes: true, text: 'Basic search & filters' },
                  { yes: false, text: 'VibeMate lifestyle matching' },
                  { yes: false, text: 'Rent rewards program' },
                  { yes: false, text: 'Direct landlord messaging' },
                  { yes: false, text: 'Priority listing alerts' },
                ]}
                ctaLabel="Get started free" ctaFree
                onCta={() => window.location.href = '/register/student'}
              />
              <TierCard
                featured
                label="All features unlocked" name="VibeMate"
                price="$7.99" period="/month" note="Founding members pay $6.39/mo forever"
                desc="Unlock lifestyle matching, rent rewards, and every feature on the platform with one simple subscription."
                features={[
                  { yes: true, text: 'Everything in Free' },
                  { yes: true, text: 'VibeMate roommate matching', note: 'AI-powered lifestyle compatibility' },
                  { yes: true, text: 'Rent rewards — earn 1pt per $1 rent' },
                  { yes: true, text: 'Direct landlord messaging' },
                  { yes: true, text: 'Priority new listing alerts' },
                  { yes: true, text: 'Advanced search & filters' },
                  { yes: true, text: 'Verified renter badge' },
                  { yes: true, text: '1 free virtual inspection/month' },
                ]}
                ctaLabel={loading === 'student_vibemate' ? 'Redirecting…' : 'Start VibeMate — $7.99/mo'}
                loading={loading === 'student_vibemate'}
                onCta={() => startCheckout('student_vibemate')}
              />
            </div>
            <VirtualInspectionsCard
              desc="Book a live video walkthrough with the landlord — inspect any property remotely before committing."
              onCta={() => startCheckout('virtual_inspection')}
              loading={loading === 'virtual_inspection'}
            />
          </div>
        )}

        {/* ══════════════════ PROFESSIONAL ══════════════════ */}
        {activeTab === 'pro' && (
          <div className="space-y-8">
            <FoundingCard
              title="Pay $10 once. Save on VibeMate forever."
              desc="Founding professionals lock in 20% off VibeMate — paying $10.39/month for life instead of $12.99. Plus a 2,500 point bonus on day one."
              perks={['20% off VibeMate forever', '2,500 bonus reward points', 'Founding member badge', 'Early platform access']}
              price="$10" priceSub="AUD · pay once, benefit forever"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <TierCard
                label="Always free" name="Free"
                price="$0" period="/month" note="No credit card required"
                desc="Find and book verified rentals across Australia at zero cost."
                features={[
                  { yes: true,  text: 'Create renter profile' },
                  { yes: true,  text: 'Browse all verified listings' },
                  { yes: true,  text: 'Book properties' },
                  { yes: true,  text: 'Basic search & filters' },
                  { yes: false, text: 'VibeMate lifestyle matching' },
                  { yes: false, text: 'Rent rewards program' },
                  { yes: false, text: 'Priority listing access' },
                  { yes: false, text: 'Direct landlord messaging' },
                ]}
                ctaLabel="Get started free" ctaFree
                onCta={() => window.location.href = '/register/student'}
              />
              <TierCard
                featured
                label="All features unlocked" name="VibeMate"
                price="$12.99" period="/month" note="Founding members pay $10.39/mo forever"
                desc="Get matched to rentals by lifestyle, commute, and budget — not just price and bedrooms."
                features={[
                  { yes: true, text: 'Everything in Free' },
                  { yes: true, text: 'VibeMate lifestyle matching', note: 'Commute, budget & lifestyle fit' },
                  { yes: true, text: 'Rent rewards — earn 1pt per $1 rent' },
                  { yes: true, text: 'Priority access to new listings' },
                  { yes: true, text: 'Direct landlord messaging' },
                  { yes: true, text: 'Advanced filters — commute & transport' },
                  { yes: true, text: 'Verified renter badge' },
                  { yes: true, text: '1 free virtual inspection/month' },
                ]}
                ctaLabel={loading === 'professional_vibemate' ? 'Redirecting…' : 'Start VibeMate — $12.99/mo'}
                loading={loading === 'professional_vibemate'}
                onCta={() => startCheckout('professional_vibemate')}
              />
            </div>
            <VirtualInspectionsCard
              desc="Perfect for interstate moves or busy professionals — inspect remotely via live video walkthrough."
              onCta={() => startCheckout('virtual_inspection')}
              loading={loading === 'virtual_inspection'}
            />
          </div>
        )}

        {/* ══════════════════ LANDLORD ══════════════════ */}
        {activeTab === 'landlord' && (
          <div className="space-y-8">
            {/* Commission card */}
            <div className="rounded-2xl border border-gray-200 p-6 sm:p-8 bg-gray-50">
              <div className="flex flex-col sm:flex-row gap-6 items-start">
                <div className="flex-1">
                  <p className="text-xs font-bold uppercase tracking-widest text-[#2cbdc4] mb-2">How landlords pay</p>
                  <h3 className="text-xl font-black text-gray-900 mb-2">List for free. Pay only when you find a tenant.</h3>
                  <p className="text-sm text-gray-500 leading-relaxed mb-5">
                    No setup fees. No monthly listing charges. RentalX takes a commission only when a tenancy is successfully placed through the platform — cheaper than a traditional letting agent.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { label: 'RentalX', val: '5–8%', highlight: true },
                      { label: 'Letting agent', val: '7–15%' },
                      { label: 'Property manager', val: '8–12%' },
                    ].map(c => (
                      <div key={c.label}
                        className={`px-4 py-3 rounded-xl text-center border ${c.highlight ? 'border-[#094f6f] bg-[#094f6f]/5' : 'border-gray-200 bg-white'}`}>
                        <p className="text-xs text-gray-500 mb-1">{c.label}</p>
                        <p className={`text-lg font-black ${c.highlight ? 'text-[#094f6f]' : 'text-gray-700'}`}>{c.val}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex-shrink-0 text-center">
                  <p className="text-4xl font-black text-[#094f6f]">5–8%</p>
                  <p className="text-xs text-gray-400 mt-1">of first month&apos;s rent<br />per tenancy placed</p>
                </div>
              </div>
            </div>
            {/* Tier grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <TierCard
                label="Always free" name="Free"
                price="$0" period="/month" note="+ 8% commission per placement"
                desc="List your property and get matched with verified renters — no upfront cost, ever."
                features={[
                  { yes: true,  text: 'Unlimited property listings' },
                  { yes: true,  text: 'Verified renter profiles' },
                  { yes: true,  text: 'Booking management' },
                  { yes: true,  text: 'Standard listing visibility' },
                  { yes: false, text: 'VibeMate tenant matching' },
                  { yes: false, text: 'Featured listing placement' },
                  { yes: false, text: 'Advanced tenant screening' },
                  { yes: false, text: 'Reduced commission rate' },
                ]}
                ctaLabel="List for free" ctaFree
                onCta={() => window.location.href = '/register/landlord'}
              />
              <TierCard
                featured
                label="All features unlocked" name="VibeMate"
                price="$19.99" period="/month" note="+ reduced 5% commission per placement"
                desc="Get matched to quality, verified tenants faster — and pay less commission on every successful placement."
                features={[
                  { yes: true, text: 'Everything in Free' },
                  { yes: true, text: 'VibeMate tenant matching', note: 'AI matches tenants to your property profile' },
                  { yes: true, text: 'Featured listing placement', note: 'Priority in search results' },
                  { yes: true, text: 'Advanced tenant screening' },
                  { yes: true, text: 'Reduced 5% commission', note: 'Save 3% on every placement vs free tier' },
                  { yes: true, text: 'Verified landlord badge' },
                  { yes: true, text: 'Tenant analytics dashboard' },
                  { yes: true, text: 'Rewards points on placements', note: 'Earn 3,750 pts per tenant placed' },
                ]}
                ctaLabel={loading === 'landlord_vibemate' ? 'Redirecting…' : 'Start VibeMate — $19.99/mo'}
                loading={loading === 'landlord_vibemate'}
                onCta={() => startCheckout('landlord_vibemate')}
              />
            </div>
          </div>
        )}

        {/* ── CTA band ── */}
        <div className="mt-16 rounded-3xl p-10 text-white text-center"
          style={{ background: 'linear-gradient(135deg,#041e2b,#094f6f)' }}>
          <h2 className="text-3xl font-black mb-3">One platform. Every type of renter.</h2>
          <p className="text-white/60 mb-6">Join the founding member cohort and lock in your price before we launch.</p>
          <Link to="/founding"
            className="inline-flex items-center gap-2 bg-white text-[#094f6f] px-7 py-3.5 rounded-xl font-bold hover:opacity-90 transition-opacity">
            Claim founding member pricing →
          </Link>
        </div>

        {/* ── FAQ ── */}
        <div className="mt-16">
          <h2 className="text-3xl font-black text-gray-900 mb-8 text-center">Common questions</h2>
          <div className="space-y-2 max-w-2xl mx-auto">
            {FAQS.map((f, i) => (
              <div key={i} className="border border-gray-200 rounded-2xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors">
                  <span className="font-semibold text-gray-900 text-sm pr-4">{f.q}</span>
                  {openFaq === i ? <ChevronUp className="w-4 h-4 text-[#2cbdc4] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Reusable sub-components ───────────────────────────────────────────────────

function FoundingCard({ title, desc, perks, price, priceSub }) {
  return (
    <div className="rounded-2xl border-2 border-[#094f6f]/20 bg-gradient-to-br from-[#094f6f]/5 to-[#2cbdc4]/5 p-6 sm:p-8 flex flex-col sm:flex-row gap-6">
      <div className="flex-1">
        <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#2cbdc4] bg-[#2cbdc4]/10 px-3 py-1 rounded-full mb-3">
          ⚡ Limited — Founding Member
        </span>
        <h3 className="text-xl font-black text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-4">{desc}</p>
        <div className="flex flex-wrap gap-2">
          {perks.map(p => (
            <span key={p} className="text-xs font-semibold text-[#094f6f] bg-[#094f6f]/8 px-3 py-1 rounded-full border border-[#094f6f]/20">
              ✓ {p}
            </span>
          ))}
        </div>
      </div>
      <div className="flex-shrink-0 flex flex-col items-center justify-center bg-white rounded-2xl border border-gray-200 px-8 py-6 text-center shadow-sm min-w-[260px]">
        <p className="text-xs text-gray-400 font-medium mb-1">One-time fee</p>
        <p className="text-5xl font-black text-gray-900 mb-1">{price}</p>
        <p className="text-xs text-gray-400 mb-4">{priceSub}</p>
        <Link to="/founding"
          className="block text-center text-sm font-bold text-white px-6 py-3 rounded-xl hover:opacity-90 transition-opacity whitespace-nowrap"
          style={{ background: BRAND }}>
          Become a Founding Member
        </Link>
      </div>
    </div>
  );
}

function TierCard({ featured, label, name, price, period, note, desc, features, ctaLabel, ctaFree, onCta, loading }) {
  return (
    <div className={`relative rounded-3xl overflow-hidden border flex flex-col transition-all ${
      featured ? 'border-[#2cbdc4] shadow-xl' : 'border-gray-200 shadow-sm hover:shadow-md'
    }`}>
      {featured && (
        <div className="h-1 w-full" style={{ background: BRAND }} />
      )}
      <div className="p-6 flex-1 flex flex-col">
        <div className="mb-6">
          <span className={`text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${featured ? 'text-[#2cbdc4] bg-[#2cbdc4]/10' : 'text-gray-500 bg-gray-100'}`}>
            {label}
          </span>
          <h3 className="text-2xl font-black text-gray-900 mt-3 mb-1">{name}</h3>
          <div className="flex items-end gap-1 mb-1">
            <span className={`text-4xl font-black ${featured ? 'text-[#094f6f]' : 'text-gray-700'}`}>{price}</span>
            <span className="text-gray-400 text-sm mb-1">{period}</span>
          </div>
          <p className="text-xs text-gray-400 mb-3">{note}</p>
          <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
        </div>
        <ul className="flex-1 space-y-0 mb-6">
          {features.map((f, i) => (
            <Feat key={i} yes={f.yes} note={f.note}>{f.text}</Feat>
          ))}
        </ul>
        <button onClick={onCta} disabled={loading}
          className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all disabled:opacity-60 flex items-center justify-center gap-2 ${
            ctaFree
              ? 'border-2 border-[#094f6f]/30 text-[#094f6f] hover:border-[#094f6f] hover:bg-[#094f6f]/5'
              : 'text-white hover:opacity-90'
          }`}
          style={!ctaFree ? { background: BRAND } : {}}>
          {loading && <Loader className="w-4 h-4 animate-spin" />}
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}

function VirtualInspectionsCard({ desc, onCta, loading }) {
  return (
    <div className="rounded-2xl border border-gray-200 p-6 flex flex-col sm:flex-row gap-6 items-start bg-gray-50">
      <div className="flex-1">
        <span className="text-xs font-bold uppercase tracking-widest text-gray-400 bg-gray-200 px-3 py-1 rounded-full">Add-on · All users</span>
        <h4 className="text-lg font-black text-gray-900 mt-3 mb-2">Virtual Property Inspections</h4>
        <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
        <p className="text-sm font-semibold text-[#2cbdc4] mt-2">VibeMate members get 1 free inspection per month included.</p>
      </div>
      <div className="flex-shrink-0 flex flex-col items-center gap-2 bg-white rounded-2xl border border-gray-200 px-6 py-5 shadow-sm text-center min-w-[150px]">
        <p className="text-3xl font-black text-gray-900">$19.99</p>
        <p className="text-xs text-gray-400">bundle of 3 inspections</p>
        <button onClick={onCta} disabled={loading}
          className="w-full text-sm font-bold text-white py-2 rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-1.5 disabled:opacity-60"
          style={{ background: BRAND }}>
          {loading && <Loader className="w-3.5 h-3.5 animate-spin" />}
          Buy bundle
        </button>
        <p className="text-xs text-gray-400">= $6.66 per inspection</p>
      </div>
    </div>
  );
}
